// bfsg

import * as React from 'react'
import styled from 'styled-components'
import theme from 'theme'
import CloseIcon from 'assets/closeOutline.svg'
import CopyIcon from 'assets/copy.svg'
import LinkIcon from 'assets/link.svg'
import MailIcon from 'assets/mail.svg'
import PrintIcon from 'assets/print.svg'
import SpinnerAtom from 'theme/atoms/Spinner'
import { config as cfg } from '../share-cart-config'
import useTranslations from 'hooks/useTranslations'
import {
  shareCartCopyLink,
  shareCartDownload,
  shareCartSendViaEmail
} from '../events'
import { useRef } from 'react'

type Props = {
  setShowModal: (val: boolean) => void
  loading: boolean
  docId: string
}

export default function Modal(props: Props) {
  const t = useTranslations<'molecules-account-CartSummary'>()
  const [copyIndicatorOne, setCopyIndicatorOne] = React.useState(false)
  const [copyIndicatorTwo, setCopyIndicatorTwo] = React.useState(false)
  const modalRef = useRef<HTMLDialogElement | null>(null)

  const copyToClipboard = (arg?: 'upper') => {
    const base = window.location.origin
    navigator.clipboard.writeText(base + cfg.shareCart.url + props.docId)
    if (arg === 'upper') {
      !copyIndicatorOne && setCopyIndicatorOne(true)
      setTimeout(() => {
        setCopyIndicatorOne(false)
      }, 1500)
    } else {
      !copyIndicatorTwo && setCopyIndicatorTwo(true)
      setTimeout(() => {
        setCopyIndicatorTwo(false)
      }, 1500)
    }
  }

  React.useEffect(() => {
    if (modalRef.current) {
      modalRef.current.addEventListener('close', () => {
        props.setShowModal(false)
      })
      modalRef.current.showModal()
    }
  }, [])

  return (
    <Wrapper ref={modalRef}>
      <div className="headline-close">
        <button
          data-cy-handle="close-modal-btn"
          onClick={() => props.setShowModal(false)}
        >
          <CloseIcon />
        </button>
      </div>
      <div className="headline">
        <div>{t('sharecart_share_text')}</div>
      </div>
      <div className="sub-text">{t('sharecart_share_explain_text')}</div>
      {!props.loading ? (
        <div className="btn-share" data-cy-state="show-share-link">
          <div className="link">
            {window.location.origin + cfg.shareCart.url + props.docId}
          </div>
          <button
            className="copy"
            data-cy-handle="copy-link-btn"
            onClick={() => {
              shareCartCopyLink()
              copyToClipboard('upper')
            }}
          >
            {copyIndicatorOne ? (
              <div data-cy-state="show-copy-indicator-one" className="copy">
                {t('sharecart_copied_indicator')}
              </div>
            ) : (
              <CopyIcon />
            )}
          </button>
        </div>
      ) : (
        <div className="spinner">
          <SpinnerAtom size="small" />
        </div>
      )}
      <div className="list">
        <button
          className="btn grey"
          data-cy-handle="copy-link-btn"
          onClick={() => {
            shareCartCopyLink()
            copyToClipboard()
          }}
        >
          <LinkIcon data-cy-state="show-copy-indicator-two" />
          <div className="copy-link">
            {copyIndicatorTwo
              ? t('sharecart_copied_indicator')
              : t('sharecart_copy_link')}
          </div>
        </button>
        <button
          className="btn grey"
          data-cy-handle="open-mail-btn"
          onClick={() => {
            shareCartSendViaEmail()
            typeof window !== 'undefined' &&
              window.open(
                `mailto:?body=
              ${window.location.origin + cfg.shareCart.url + props.docId}
              `
              )
          }}
        >
          <MailIcon />
          <div>{t('sharecart_mail_incident')}</div>
        </button>
        <button
          className="btn grey"
          data-cy-handle="open-print-btn"
          onClick={() => {
            shareCartDownload()
            props.setShowModal(false)
            setTimeout(() => {
              typeof window !== 'undefined' && window.print()
            }, 100)
          }}
        >
          <PrintIcon />
          <div>{t('sharecart_print_cart')}</div>
        </button>
        <button
          className="btn close"
          data-cy-handle="close-modal-btn"
          onClick={() => props.setShowModal(false)}
        >
          {t('sharecart_close')}
        </button>
      </div>
    </Wrapper>
  )
}

const Wrapper = styled.dialog`
  ${theme._ty([13, 0, 18], theme.font, '400')}
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: ${theme.zIndex.theme_molecules_account_cartsummary_sharecart_Modal};
  width: calc(100% - 2.5rem);
  padding: ${theme.spacing('m')} ${theme.spacing('m')};
  background-color: ${theme.colors.shade.b7};

  @media (min-width: ${theme.ms.MD}px) {
    ${theme._ty([16, 0, 24], theme.font, '400')}
  }

  @media (min-width: ${theme.ms.SM}px) {
    width: calc(100% - 5rem);
  }

  @media (min-width: ${theme.ms.MD}px) {
    width: 37.5rem;
  }

  @media (min-width: ${theme.ms.LG}px) {
    width: 51.25rem;
  }
  > .headline-close {
    width: 100%;
    display: flex;
    justify-content: end;
    margin-bottom: -${theme.spacing('xs')};
    cursor: pointer;
  }

  > .headline {
    ${theme._ty([16, 0, 22], theme.fontSpecial, '700')}
    margin-bottom: ${theme.spacing('xs')};

    @media (min-width: ${theme.ms.MD}px) {
      ${theme._ty([22, 0, 28], theme.fontSpecial, '700')}
    }
  }

  > .sub-text {
    margin-bottom: ${theme.spacing('m')};
  }

  > .list {
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing('xs')};

    @media (min-width: ${theme.ms.MD}px) {
      flex-direction: row;
    }

    > .btn {
      display: flex;
      justify-content: center;
      align-items: center;
      padding: ${theme.spacing('s')};
      color: ${theme.colors.shade.b2};
      border: 1px solid ${theme.colors.shade.b3};
      ${theme.rounding('m')}
      cursor: pointer;

      ${theme._ty([16, 0, 22], theme.fontSpecial, '700')}
    }
    > .grey {
      gap: 0.4375rem;

      > .copy-link {
        display: flex;
        min-width: 5.4375rem;
        justify-content: center;
        color: ${theme.colors.shade.b2};
        ${theme._ty([16, 0, 22], theme.fontSpecial, '700')}
      }
    }
    > .close {
      color: ${theme.colors.b0};
      border: 0;
      padding-top: ${theme.spacing('xxs')};
      padding-bottom: 0;
      @media (min-width: ${theme.ms.MD}px) {
        display: none;
      }
    }
  }
  > .btn-share {
    display: flex;
    align-items: center;
    border: 1px solid ${theme.colors.shade.b3};
    ${theme.rounding('m')}
    justify-content: space-between;
    background-color: ${theme.colors.shade.primaryUltraBright};
    ${theme.ty('r-base', '400')};
    ${theme._ty([16, 0, 24], theme.font, '400')}

    color: ${theme.colors.b0};
    margin-bottom: ${theme.spacing('ml')};
    padding: ${theme.spacing('xxs')} ${theme.spacing('s')};
    min-height: 2.875rem;
    > .link {
      flex-basis: auto;
    }
    > .copy {
      ${theme._ty([16, 0, 22], theme.fontSpecial, '700')}
      color: ${theme.colors.shade.b2};
      flex-basis: 3.125rem;
      text-align: right;
      > svg {
        cursor: pointer;
        width: 1.5rem;
      }
    }
  }
  > .spinner {
    display: flex;
    justify-content: center;
    min-height: 2.875rem;
    margin-bottom: ${theme.spacing('ml')};
  }
`
